import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LifeBuoy, Plus, Clock, AlertTriangle, CheckCircle2, MessagesSquare, ListChecks } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PageHeader, KPIGrid, KPICard, EmptyState } from "@/components/app/PageKit";

const PRIO_CFG = {
  critical: { bg: "bg-red-100 text-red-800 border-red-200" },
  high: { bg: "bg-orange-100 text-orange-800 border-orange-200" },
  medium: { bg: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { bg: "bg-slate-100 text-slate-700 border-slate-200" },
};
const STATUS_CFG = {
  open: { bg: "bg-cyan-100 text-cyan-800" },
  in_progress: { bg: "bg-violet-100 text-violet-800" },
  resolved: { bg: "bg-emerald-100 text-emerald-800" },
  closed: { bg: "bg-slate-100 text-slate-700" },
};

export default function Helpdesk() {
  const { user } = useAuth();
  const isAgent = ["super_admin", "hr_admin", "it_support", "admin_ops"].includes(user?.role);
  const [tickets, setTickets] = useState([]);
  const [emps, setEmps] = useState([]);
  const [tab, setTab] = useState("mine");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "it", priority: "medium" });
  const [cmt, setCmt] = useState("");

  const load = async () => {
    const [t, e] = await Promise.all([api.get("/helpdesk/tickets", { params: { scope: tab === "mine" ? "mine" : "all" } }), api.get("/employees")]);
    setTickets(t.data); setEmps(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const create = async () => {
    try { await api.post("/helpdesk/tickets", form); toast.success("Ticket raised"); setOpen(false); setForm({ title: "", description: "", category: "it", priority: "medium" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (status) => {
    try { await api.patch(`/helpdesk/tickets/${detail.id}`, { status }); toast.success(`Marked ${status}`); setDetail(null); load(); }
    catch { toast.error("Failed"); }
  };
  const assign = async (aid) => {
    try { await api.patch(`/helpdesk/tickets/${detail.id}`, { assignee_id: aid }); toast.success("Assigned"); load(); setDetail({ ...detail, assignee_id: aid }); } catch { toast.error("Failed"); }
  };
  const comment = async () => {
    if (!cmt.trim()) return;
    try { await api.patch(`/helpdesk/tickets/${detail.id}`, { comment: cmt }); setCmt(""); const r = await api.get("/helpdesk/tickets", { params: { scope: tab === "mine" ? "mine" : "all" } }); setTickets(r.data); setDetail(r.data.find(t => t.id === detail.id)); } catch { toast.error("Failed"); }
  };

  const stats = {
    open: tickets.filter(t => t.status === "open").length,
    in_progress: tickets.filter(t => t.status === "in_progress").length,
    resolved: tickets.filter(t => t.status === "resolved").length,
    overdue: tickets.filter(t => t.sla_deadline && new Date(t.sla_deadline) < new Date() && !["resolved", "closed"].includes(t.status)).length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="helpdesk-page">
      <PageHeader
        category="Services"
        title="Helpdesk"
        description="Raise IT, HR, Finance or Facilities tickets — track SLA, assignment, and resolution in one place."
        primaryAction={{ label: "Raise ticket", icon: Plus, onClick: () => setOpen(true), testid: "new-ticket-btn" }}
      />
      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>New support ticket</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1" data-testid="ticket-title"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">IT</SelectItem><SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="facilities">Facilities</SelectItem><SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1" rows={4}/></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.title} className="qh-grad-bg text-white" data-testid="ticket-submit">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>

      <KPIGrid cols={4}>
        <KPICard icon={MessagesSquare} tone="cyan"    label="Open"         value={stats.open} testid="kpi-open" />
        <KPICard icon={Clock}          tone="violet"  label="In Progress"  value={stats.in_progress} testid="kpi-progress" />
        <KPICard icon={CheckCircle2}   tone="emerald" label="Resolved"     value={stats.resolved} testid="kpi-resolved" />
        <KPICard icon={AlertTriangle}  tone="rose"    label="Overdue SLA"  value={stats.overdue} sub="needs attention" testid="kpi-overdue" />
      </KPIGrid>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="mine">My tickets</TabsTrigger>
          {isAgent && <TabsTrigger value="all">All tickets</TabsTrigger>}
        </TabsList>
        <TabsContent value={tab} className="mt-4 space-y-2">
          {tickets.length === 0 ? (
            <EmptyState
              icon={LifeBuoy}
              title={tab === "mine" ? "No tickets yet" : "No tickets to triage"}
              message={tab === "mine" ? "When you raise an IT, HR or Finance ticket, it'll show up here with SLA tracking." : "Once team members raise tickets, they'll appear here for assignment."}
              action={tab === "mine" ? { label: "Raise ticket", icon: Plus, onClick: () => setOpen(true) } : undefined}
              testid="helpdesk-empty"
            />
          ) : tickets.map((t) => (
            <Card key={t.id} className="p-4 border-slate-200 shadow-none hover:border-cyan-400 transition cursor-pointer" onClick={() => setDetail(t)} data-testid={`ticket-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-md ${PRIO_CFG[t.priority]?.bg || "bg-slate-100"}`}>
                  <LifeBuoy className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-900">{t.title}</div>
                    <Badge className={`${STATUS_CFG[t.status]?.bg || "bg-slate-100"} border-0 capitalize`}>{t.status?.replace("_", " ")}</Badge>
                    <Badge variant="outline" className="capitalize text-[10px]">{t.category}</Badge>
                    <Badge className={`${PRIO_CFG[t.priority]?.bg} border capitalize`}>{t.priority}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</div>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                    <span>From: <strong>{t.requester?.full_name || "—"}</strong></span>
                    {t.assignee && <span>· Assigned to: <strong>{t.assignee.full_name}</strong></span>}
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3"/>SLA {new Date(t.sla_deadline).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`${STATUS_CFG[detail.status]?.bg} border-0 capitalize`}>{detail.status?.replace("_", " ")}</Badge>
                <Badge variant="outline" className="capitalize">{detail.category}</Badge>
                <Badge className={`${PRIO_CFG[detail.priority]?.bg} border capitalize`}>{detail.priority}</Badge>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</div>

              {isAgent && (
                <div className="p-3 bg-slate-50 rounded-md space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Assign to</Label>
                  <Select value={detail.assignee_id || ""} onValueChange={assign}>
                    <SelectTrigger><SelectValue placeholder="Select agent"/></SelectTrigger>
                    <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    {detail.status !== "in_progress" && <Button size="sm" variant="outline" onClick={() => updateStatus("in_progress")}>Mark in progress</Button>}
                    {detail.status !== "resolved" && <Button size="sm" variant="outline" onClick={() => updateStatus("resolved")}><CheckCircle2 className="w-3 h-3 mr-1"/>Resolve</Button>}
                    {detail.status !== "closed" && <Button size="sm" variant="ghost" onClick={() => updateStatus("closed")}>Close</Button>}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-wider">Comments</Label>
                <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                  {(detail.comments || []).length === 0 ? <div className="text-xs text-slate-400">No comments yet.</div> :
                    detail.comments.map((c, i) => (
                      <div key={i} className="p-2 bg-slate-50 rounded-md text-sm">
                        <div className="text-[11px] text-slate-500">{c.by} · {new Date(c.at).toLocaleString()}</div>
                        <div className="text-slate-800">{c.text}</div>
                      </div>
                    ))
                  }
                </div>
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Add comment…" value={cmt} onChange={(e) => setCmt(e.target.value)}/>
                  <Button onClick={comment}>Send</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
